// Patient PWA home — the aligner patient's own treatment: progress, current tray + next change,
// and a server-backed daily wear timer with a 7-day compliance strip. One login per case; the
// server resolves the case from the session, so nothing here names a case id. Live via SSE.
//
// This is the one screen in the product that is only ever seen on a phone, held one-handed, often
// last thing at night. So: one decision per screenful, the timer as the hero, a thumb-height
// primary action, and safe-area padding so the button never sits under the home indicator.
import { useCallback, useEffect, useState } from 'react';
import { formatDate, type PatientTreatment, type PatientWear } from '@alignova/shared';
import { useAuth } from '../auth/AuthContext.tsx';
import { AlignovaLockup } from '../ui/AlignovaLogo.tsx';
import { IconClock, IconPause, IconPlay, IconSignOut, IconTray } from '../ui/icons.tsx';
import { Skeleton } from '../ui/mobile.tsx';
import { patientApi } from '../lib/patientApi.ts';
import { PatientPhotoCheck } from './PatientPhotoCheck.tsx';
import { useLiveEvent } from '../lib/useLive.ts';

const fmtHM = (sec: number) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
const dow = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' });

export function PatientHome() {
  const { user, logout } = useAuth();
  const [t, setT] = useState<PatientTreatment | null>(null);
  const [w, setW] = useState<PatientWear | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    patientApi.me().then(setT).catch(() => setErr('Could not load your treatment. Check your connection and try again.'));
    patientApi.wear().then(setW).catch(() => undefined);
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);

  // Tick once a second while the timer runs so the display counts up live.
  useEffect(() => {
    if (!w?.running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [w?.running]);

  const liveSeconds =
    w ? w.todaySeconds + (w.running && w.runStartedAt ? Math.max(0, (now - Date.parse(w.runStartedAt)) / 1000) : 0) : 0;
  const target = (w?.targetHours ?? 22) * 3600;
  const pct = Math.min(100, Math.round((liveSeconds / target) * 100));

  const toggle = async () => {
    if (!w) return;
    setBusy(true);
    try {
      setW(w.running ? await patientApi.stopWear() : await patientApi.startWear());
      setNow(Date.now());
    } finally {
      setBusy(false);
    }
  };

  if (err)
    return (
      <div className="mx-auto max-w-xl p-6">
        <div className="rounded-lg border border-status-urgent-fg/30 bg-status-urgent-bg px-4 py-3 text-sm text-status-urgent-fg">
          {err}
        </div>
        <button onClick={load} className="tap mt-3 rounded-md border border-line px-4 py-2.5 text-sm font-medium text-ink">
          Try again
        </button>
      </div>
    );
  if (!t)
    return (
      <div className="mx-auto max-w-xl space-y-4 p-4">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-56 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );

  const trayPct = t.totalTrays && t.currentTray ? Math.round((t.currentTray / t.totalTrays) * 100) : 0;
  const changeLabel =
    t.daysToChange == null
      ? null
      : t.daysToChange > 0
        ? `Change in ${t.daysToChange} day${t.daysToChange === 1 ? '' : 's'}`
        : t.daysToChange === 0
          ? 'Change your tray today'
          : `Tray change overdue by ${-t.daysToChange} day${t.daysToChange === -1 ? '' : 's'}`;

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4 pt-safe pb-10">
      <header className="flex items-start justify-between gap-3 pt-2">
        <div className="min-w-0">
          <AlignovaLockup sub="Patient App" size="sm" className="mb-2" />
          <h1 className="text-display text-ink">Hi, {t.patientName || user?.displayName}</h1>
          <p className="mt-1 text-xs text-muted">
            Case {t.caseNumber}
            {t.clinicName ? ` · ${t.clinicName}` : ''}
          </p>
        </div>
        <button
          onClick={() => void logout()}
          aria-label="Sign out"
          className="tap grid h-10 w-10 shrink-0 place-items-center rounded-full border border-line text-muted"
        >
          <IconSignOut size={18} />
        </button>
      </header>

      {/* Wear timer - the hero. The ring reads from across the room; the number underneath is the
          detail you check up close. */}
      <section
        className="grain animate-rise relative overflow-hidden rounded-xl p-5 text-white shadow-level-2"
        style={{ background: 'linear-gradient(160deg, var(--c-brand), var(--c-brand-deep))' }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-5">
            <Ring pct={pct} />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-white/70">
                <IconClock size={14} />
                Today&rsquo;s wear
              </div>
              <div className="tabnum mt-1 text-[2.6rem] font-extrabold leading-none">{fmtHM(liveSeconds)}</div>
              <div className="mt-1 text-sm text-white/80">
                of {w?.targetHours ?? 22}h goal · <span className="tabnum">{pct}%</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => void toggle()}
            disabled={busy || !w}
            className="tap mt-5 flex w-full items-center justify-center gap-2 rounded-lg py-3.5 text-base font-bold text-navy shadow-level-1 transition disabled:opacity-60"
            style={{ backgroundColor: w?.running ? '#FFFFFF' : 'var(--c-brand-warm)' }}
          >
            {w?.running ? <IconPause size={20} /> : <IconPlay size={20} />}
            {w?.running ? 'I took my aligners out' : 'I put my aligners in'}
          </button>
          <p className="mt-2 text-center text-xs text-white/70">
            {w?.running
              ? 'Timer running - tap when you remove them to eat or brush.'
              : 'The timer is stopped. Start it when your aligners go back in.'}
          </p>
        </div>
      </section>

      <PatientPhotoCheck treatment={t} />

      {/* Tray progress */}
      <section className="animate-rise rounded-xl border border-line bg-surface p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
            <IconTray size={15} />
            Your trays
          </div>
          <div className="text-xs text-muted">{t.milestone}</div>
        </div>
        {t.totalTrays ? (
          <>
            <div className="mt-2 flex items-end gap-2">
              <span className="tabnum text-display-lg text-ink">{t.currentTray ?? 1}</span>
              <span className="pb-1.5 text-muted">of {t.totalTrays}</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${trayPct}%`, backgroundColor: 'var(--c-brand)' }}
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <Info
                label="Next change"
                value={t.nextChangeDate ? formatDate(t.nextChangeDate) : '-'}
                sub={changeLabel ?? undefined}
                warn={(t.daysToChange ?? 1) <= 0}
              />
              <Info
                label="Est. finish"
                value={t.estimatedCompletion ? formatDate(t.estimatedCompletion) : '-'}
                sub={`Every ${t.intervalDays} days`}
              />
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-muted">Your tray plan will appear here once the lab finalises it.</p>
        )}
      </section>

      {/* 7-day compliance */}
      <section className="animate-rise rounded-xl border border-line bg-surface p-5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Last 7 days</div>
        <div className="mt-3 flex h-28 items-end gap-2">
          {(w?.days ?? []).map((d) => {
            const secs = d.day === (w?.days.at(-1)?.day ?? '') ? liveSeconds : d.seconds;
            const h = Math.min(100, Math.round((secs / target) * 100));
            const ok = secs >= target * 0.9;
            return (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-1.5">
                <div className="flex h-20 w-full items-end overflow-hidden rounded-md bg-surface-2">
                  <div
                    className="w-full rounded-md transition-all"
                    style={{
                      height: `${Math.max(3, h)}%`,
                      backgroundColor: ok ? 'var(--c-brand)' : 'var(--c-brand-warm)',
                    }}
                    title={fmtHM(secs)}
                  />
                </div>
                <div className="text-[10px] text-muted">{dow(d.day)}</div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 max-w-[46ch] text-xs text-muted">
          Aim for {w?.targetHours ?? 22} hours every day. Only remove your aligners to eat, to drink
          anything but water, or to brush.
        </p>
      </section>
    </div>
  );
}

function Ring({ pct }: { pct: number }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <svg width="92" height="92" viewBox="0 0 92 92" className="shrink-0" role="img" aria-label={`${pct}% of today's wear goal`}>
      <circle cx="46" cy="46" r={r} stroke="rgba(255,255,255,0.22)" strokeWidth="9" fill="none" />
      <circle
        cx="46"
        cy="46"
        r={r}
        stroke="var(--c-brand-warm)"
        strokeWidth="9"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct / 100)}
        transform="rotate(-90 46 46)"
        style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}
      />
      <text
        x="46"
        y="51"
        textAnchor="middle"
        fontSize="16"
        fontWeight="700"
        fill="#fff"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {pct}%
      </text>
    </svg>
  );
}

function Info({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className="rounded-md bg-surface-2 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted">{label}</div>
      <div className="text-base font-semibold text-ink">{value}</div>
      {sub && <div className={`text-xs ${warn ? 'font-medium text-status-warn-fg' : 'text-muted'}`}>{sub}</div>}
    </div>
  );
}
